/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

// Ported over from AngelAI codebase
import { useCallback, useEffect, useState } from 'react';

export type TWindowDimensions = {
  innerWidth: number;
  innerHeight: number;
  isMobile: boolean;
  isPortrait: boolean;
};

/**
 * Hook that returns the current window dimensions as an object with properties
 * `innerWidth` and `innerHeight`. The dimensions are updated when the window is
 * resized or its orientation is changed.
 * @returns {Object} - The current window dimensions
 * @returns {number} .innerWidth - The current width of the window's content area
 * @returns {number} .innerHeight - The current height of the window's content area
 * @returns {boolean} .isDesktop - Whether the innerWidth is greater than 900px
 * @returns {boolean} .isPortrait - Whether the innerWidth/innerHeight is more narrow than 16/10
 */
const useWindowDimensions = (onChange?: (dimensions: TWindowDimensions) => void) => {
  // Initialize the state with the current window dimensions
  const [windowDimensions, setWindowDimensions] = useState<TWindowDimensions>({
    innerWidth: window.innerWidth || 0,
    innerHeight: window.innerHeight || 0,
    isMobile: (window.innerWidth || 0) < Number(process.env[`REACT_APP_MOBILE_SCREEN_CUTOFF`]),
    isPortrait: window.innerHeight > 0 ? (window.innerWidth || 0) / window.innerHeight < 1.6 : false,
  });

  // useCallback caches the function based on the dependencies so it isn't redefined each render
  const handleChange = useCallback(() => {
    const dimensions: TWindowDimensions = {
      innerWidth: window.innerWidth || 0,
      innerHeight: window.innerHeight || 0,
      isMobile: (window.innerWidth || 0) < Number(process.env[`REACT_APP_MOBILE_SCREEN_CUTOFF`]),
      isPortrait: window.innerHeight > 0 ? (window.innerWidth || 0) / window.innerHeight < 1.6 : false,
    };
    setWindowDimensions(dimensions);

    if (typeof onChange === 'function') {
      onChange(dimensions);
    }
  }, [onChange]);

  // Update the state when the window is resized or its orientation is changed
  useEffect(() => {
    handleChange();
    window.addEventListener('resize', handleChange);
    window.addEventListener('orientationchange', handleChange);

    // Remove event listeners when the component is unmounted
    return () => {
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('orientationchange', handleChange);
    };
  }, [handleChange]);

  return windowDimensions;
};

export default useWindowDimensions;
